import React, { useEffect, useState } from 'react';
import Card from '../../../components/common/Card';
import MemberAdditionalDocumentTable from '../../../components/tables/MemberAdditionalDocumentTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberAdditionalDocumentApi } from '../../../hooks/useMemberAdditionalDocumentApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete, showAlert } from '../../../utils/sweetAlertUtils';
import { DocumentTypeDTO, MemberAdditionalDocumentDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { getMemberId } from '../../../utils/tokenUtils';
import { useDocumentTypeApi } from '../../../hooks/useDocumentTypeApi';
import { useFileApi } from '../../../hooks/useFileApi';
import { FileViewer } from '../../../components/common/FileViewer';

const MemberAdditionalDocumentList: React.FC = () => {
  const { search, remove, isLoading } = useMemberAdditionalDocumentApi();
  const [items, setItems] = useState<MemberAdditionalDocumentDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [sortBy, setSortBy] = useState<string>('CreatedUtc');  
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeDTO[]>([]);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);
  const [sortDescending, setSortDescending] = useState<boolean>(true);

  const memberId = getMemberId();
  const { search: searchDocumentTypes, isLoading: isLoadingDocumentTypes } = useDocumentTypeApi();
  const { getFileUrl } = useFileApi();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Member Additional Documents', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters: {
          memberId: memberId
        },
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Member Additional Documents:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  useEffect(() => {
    const fetchDocumentTypeOptions = async () => {
      try {
        const response = await searchDocumentTypes({
          filters: { isActive: true },
          page: 1,
          pageSize: 999,
          sortBy: 'DisplayPosition',
        });
        setDocumentTypes(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching document types:', error);
      }
    };

    fetchDocumentTypeOptions();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: MemberAdditionalDocumentDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting MemberAdditionalDocument:', error);
    }
  };

  const handleDownloadAdditionalDocument = async (dto: MemberAdditionalDocumentDTO) => {
    if (!dto.documentId) {
      console.error('No document ID found');
      showAlert({ 
        icon: 'error', 
        title: 'Error', 
        text: 'No document found to download' 
      });
      return;
    }

    try {
      const response = await getFileUrl(dto.documentId);
      
      if (!response?.data) {
        showAlert({ 
          icon: 'error', 
          title: 'Error', 
          text: 'Unable to retrieve file information' 
        });
        return;
      }

      // Check if file has been processed (virus scan complete)
      if (response.data.status !== 'Processed') {
        showAlert({ 
          icon: 'info', 
          title: 'File Processing', 
          text: 'This file is still being scanned. Please try again in a moment.' 
        });
        return;
      }

      if (response.data.url) {
        setViewingFile({
          name: response.data.fileName || dto.documentName || 'Document',
          url: response.data.url,
        });
      } else {
        showAlert({ 
          icon: 'error', 
          title: 'Error', 
          text: 'File URL not available' 
        });
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      showAlert({ 
        icon: 'error', 
        title: 'Error', 
        text: 'Failed to download the document' 
      });
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(sortKey);
      setSortDescending(true);
    }
    setCurrentPage(1);
  };

  return (
    <Card
      title="Manage Member Additional Documents"
      subtitle={`Total Records: ${totalRecords}`}
      headerActions={
        <div className="flex space-x-2">
          <AddNewButton
              label='Add Additional Document'
            onClick={() => navigate('/admin/memberadditionaldocuments/create')}
          />
        </div>
      }
    >
      <form onSubmit={handleSearch} className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <FormField
            id="documentName"
            label="Search by Document Name"
            value={searchFilters.documentName || ''}
            onChange={(e) => setSearchFilters({ ...searchFilters, documentName: e.target.value })}
            placeholder="Search by Document Name"
          />
          <FormField
            id="documentTypeId"
            label="Search by Document Type"
            value={searchFilters.documentTypeId || ''}
            type='dropdown'
            onChange={(e) => setSearchFilters({ ...searchFilters, documentTypeId: e.target.value })}
            placeholder="Search by Document Type Id"
            options={documentTypes.map((type) => ({ value: type.id, label: type.title }))}
            isLoading={isLoadingDocumentTypes}
          />

        </div>

        <div className="flex space-x-4 mb-4">
          <Button variant="secondary" onClick={handleClearSearch}>
            Clear Search
          </Button>
          <Button type="submit">Search</Button>
        </div>
      </form>

      <MemberAdditionalDocumentTable
        data={items}
        handleEdit={(dto) =>
          navigate(`/admin/memberadditionaldocuments/${dto.id}`)
        }
        handleDelete={handleDelete}
        handleDownload={handleDownloadAdditionalDocument}
        currentPage={currentPage}
        totalPages={Math.ceil(totalRecords / pageSize)}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSort={handleSort}
      />

      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}
    </Card>
  );
};

export default MemberAdditionalDocumentList;